/**
 * Browser File/Blob kept on project media after a local import.
 * Stored as a non-enumerable `file` property so JSON.stringify
 * (project save, history, media-version snapshots) skips it.
 */

export function isSourceFile(value) {
    return typeof Blob !== "undefined" && value instanceof Blob;
}

export function attachSourceFile(target, file) {
    if (!target || !isSourceFile(file)) {
        return target;
    }

    Object.defineProperty(target, "file", {
        value: file,
        writable: true,
        enumerable: false,
        configurable: true,
    });

    return target;
}

export function getSourceFile(media) {
    return isSourceFile(media?.file) ? media.file : null;
}

function sourceScheme(source) {
    if (isSourceFile(source)) {
        return "blob";
    }
    if (typeof source !== "string" || !source) {
        return "unknown";
    }
    const colon = source.indexOf(":");
    return colon > 0 ? source.slice(0, colon) : "path";
}

function formatFileSize(bytes) {
    if (!Number.isFinite(bytes) || bytes < 0) {
        return "";
    }
    const mb = bytes / (1024 * 1024);
    if (mb < 0.1) {
        return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    }
    return `${mb.toFixed(1)} MB`;
}

export function describeMediaFetchError(error, source) {
    const message = error?.message || String(error || "Unknown error");
    if (/\(source /.test(message)) {
        return message;
    }

    const scheme = sourceScheme(source);
    const size = isSourceFile(source) ? source.size : undefined;
    const details = [`source ${scheme}`];

    if (Number.isFinite(size)) {
        const formatted = formatFileSize(size);
        if (formatted) {
            details.push(`size ${formatted}`);
        }
    }

    const lower = message.toLowerCase();
    if (lower.includes("failed to fetch") || error?.name === "TypeError") {
        if (scheme === "blob" || scheme === "file" || scheme === "path") {
            details.push(
                "the local media file could not be read; it may be too large, moved, or no longer accessible",
            );
        } else if (scheme === "http" || scheme === "https") {
            details.push(
                "the media URL could not be downloaded; this is often a CORS restriction in the browser",
            );
        }
    }

    return `${message} (${details.join("; ")})`;
}

/**
 * Payload for Firebase Storage uploadBytesResumable.
 * Web local imports upload the original File directly (no fetch + arrayBuffer).
 * Electron still reads the ffmpeg-extracted WAV from disk.
 */
export async function resolveLocalTranscriptionUpload({
    electron,
    extractedAudioPath,
    media,
}) {
    if (!electron) {
        const file = getSourceFile(media);
        if (file) {
            return file;
        }
    }

    const source = electron ? extractedAudioPath : media?.path;

    try {
        const response = await fetch(source);
        if (!response.ok) {
            throw new Error(`HTTP ${response.status} ${response.statusText}`);
        }
        return await response.arrayBuffer();
    } catch (error) {
        throw new Error(describeMediaFetchError(error, source));
    }
}
